import { useEffect, useMemo, useState } from 'react';
import { FileBarChart } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { getEmpresa } from '../services/empresaService';
import { getObras } from '../services/obraService';
import { getConcretagemRealizada } from '../services/concretagemRealizadaService';
import { getResultadoSequencialData, normalizeResultadoSequencial, buildCabecalho, getConcretagensComResultado } from '../services/resultadoSequencialService';
import EnsaioCompressaoPDF from '../components/EnsaioCompressaoPDF';
import usePersonalizacaoRelatorio from '../hooks/usePersonalizacaoRelatorio';

// Normaliza uma empresa da API para { id, nome, idUsuarioAssinatura1, idUsuarioAssinatura2 }.
function normalizeEmpresa(item) {
  return {
    id: String(item.idEmpresa || item.id || ''),
    nome: item.razaoSocial || item.nomeEmpresa || item.nome || '-',
    idUsuarioAssinatura1: String(item.idUsuarioAssinatura1 || item.id_usuario_assinatura_1 || item.usuarioAssinatura1?.idUsuario || ''),
    idUsuarioAssinatura2: String(item.idUsuarioAssinatura2 || item.id_usuario_assinatura_2 || item.usuarioAssinatura2?.idUsuario || ''),
  };
}

// Normaliza uma obra da API para { id, nome, idEmpresa, flagRelatorioCor, idContatoTecnico }.
function normalizeObra(item) {
  return {
    id: String(item.idObra || item.id || ''),
    nome: item.nomeObra || item.nome || '-',
    idEmpresa: String(item.idEmpresa || item.empresa?.idEmpresa || ''),
    flagRelatorioCor: Boolean(item.flagRelatorioCor ?? item.flag_relatorio_cor),
    idContatoTecnico: String(
      item.idContatoTecnico || item.contatoTecnico?.idContato || item.ContatoTecnico?.IdContato || ''
    ),
  };
}


// Normaliza uma concretagem da API para { id, idObra, dataConcretagem }.
function normalizeConcretagem(item) {
  return {
    id: String(item.id || item.idConcretagemRealizada || ''),
    idObra: String(item.obra?.idObra || item.idObra || ''),
    dataConcretagem: item.dataConcretagem || item.data_concretagem || '',
  };
}

// Exibe data (e hora, se houver) no padrão brasileiro.
function formatDataHora(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  const dataStr = parsed.toLocaleDateString('pt-BR');
  const horaStr = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  return horaStr === '00:00' ? dataStr : `${dataStr} às ${horaStr}`;
}

// Relatório "Resultados sequencial": seleciona empresa + obra, escolhe datas de concretagem e gera o resultado.
function ResultadosSequencialPage() {
  const [empresas, setEmpresas] = useState([]);
  const [obras, setObras] = useState([]);
  const [concretagens, setConcretagens] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedEmpresaId, setSelectedEmpresaId] = useState('');
  const [selectedObraId, setSelectedObraId] = useState('');
  const [selectedConcretagemIds, setSelectedConcretagemIds] = useState([]);

  const [resultadoData, setResultadoData] = useState([]);
  const [loadingResultado, setLoadingResultado] = useState(false);
  const [erroResultado, setErroResultado] = useState('');
  const [identificacaoRelatorio, setIdentificacaoRelatorio] = useState('');

  const [concretagensElegiveisIds, setConcretagensElegiveisIds] = useState(new Set());
  const [loadingElegibilidade, setLoadingElegibilidade] = useState(false);

  // Carrega empresas, obras e concretagens em paralelo ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      const [empresasResult, obrasResult, concretagensResult] = await Promise.allSettled([
        getEmpresa(),
        getObras(),
        getConcretagemRealizada(),
      ]);

      if (!isMounted) return;

      if (empresasResult.status === 'fulfilled' && Array.isArray(empresasResult.value)) {
        setEmpresas(empresasResult.value.map(normalizeEmpresa).filter((e) => e.id));
      }
      if (obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)) {
        setObras(obrasResult.value.map(normalizeObra).filter((o) => o.id));
      }
      if (concretagensResult.status === 'fulfilled' && Array.isArray(concretagensResult.value)) {
        setConcretagens(concretagensResult.value.map(normalizeConcretagem).filter((c) => c.id));
      }

      setLoading(false);
    };

    loadData().catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  // Obras da empresa selecionada.
  const obrasDaEmpresa = useMemo(
    () => (selectedEmpresaId ? obras.filter((o) => o.idEmpresa === selectedEmpresaId) : []),
    [obras, selectedEmpresaId]
  );

  // Concretagens da obra selecionada, ordenadas por data.
  const concretagensDaObra = useMemo(() => {
    if (!selectedObraId) return [];
    return concretagens
      .filter((c) => c.idObra === selectedObraId)
      .sort((a, b) => String(a.dataConcretagem).localeCompare(String(b.dataConcretagem)));
  }, [concretagens, selectedObraId]);

  // Só exibe datas que têm resultado disponível (ou seja, que realmente geram o PDF).
  const concretagensElegiveis = useMemo(
    () => concretagensDaObra.filter((c) => concretagensElegiveisIds.has(c.id)),
    [concretagensDaObra, concretagensElegiveisIds]
  );

  // Busca, ao trocar de obra, quais datas de concretagem têm resultado disponível.
  useEffect(() => {
    if (concretagensDaObra.length === 0) {
      setConcretagensElegiveisIds(new Set());
      return;
    }
    let isMounted = true;
    setLoadingElegibilidade(true);
    getConcretagensComResultado({
      idsConcretagem: concretagensDaObra.map((c) => c.id),
      idObra: selectedObraId,
    })
      .then((ids) => { if (isMounted) setConcretagensElegiveisIds(ids); })
      .catch(() => { if (isMounted) setConcretagensElegiveisIds(new Set()); })
      .finally(() => { if (isMounted) setLoadingElegibilidade(false); });
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concretagensDaObra, selectedObraId]);

  // Ao trocar a empresa, reseta obra e datas.
  const handleEmpresaChange = (event) => {
    setSelectedEmpresaId(event.target.value);
    setSelectedObraId('');
    setSelectedConcretagemIds([]);
  };

  // Ao trocar a obra, reseta as datas.
  const handleObraChange = (event) => {
    setSelectedObraId(event.target.value);
    setSelectedConcretagemIds([]);
  };

  // Marca/desmarca uma concretagem na seleção de datas.
  const toggleConcretagem = (id) => {
    setSelectedConcretagemIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Busca os dados do relatório (Ensaio de Compressão Axial) para as concretagens selecionadas.
  useEffect(() => {
    if (selectedConcretagemIds.length === 0) {
      setResultadoData([]);
      return;
    }
    let isMounted = true;
    setLoadingResultado(true);
    setErroResultado('');
    getResultadoSequencialData({ idsConcretagem: selectedConcretagemIds, idObra: selectedObraId })
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : data?.items || [];
        setResultadoData(list.map(normalizeResultadoSequencial).filter((item) => item.id));
      })
      .catch((error) => {
        if (!isMounted) return;
        setResultadoData([]);
        setErroResultado(error?.message || 'Não foi possível carregar os resultados.');
      })
      .finally(() => { if (isMounted) setLoadingResultado(false); });
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConcretagemIds, selectedObraId]);

  const cabecalhoPdf = useMemo(
    () => ({ ...buildCabecalho(resultadoData), identificacaoRelatorio }),
    [resultadoData, identificacaoRelatorio]
  );

  const canGerar = Boolean(selectedEmpresaId && selectedObraId && selectedConcretagemIds.length > 0);

  const obraSelecionada = obras.find((o) => o.id === selectedObraId);
  const corAtiva = obraSelecionada?.flagRelatorioCor ?? true;

  const empresaSelecionada = empresas.find((e) => e.id === selectedEmpresaId);
  const {
    logoCliente,
    assinaturas,
    carregando: carregandoPersonalizacao,
  } = usePersonalizacaoRelatorio({
    idUsuario1: empresaSelecionada?.idUsuarioAssinatura1,
    idUsuario2: empresaSelecionada?.idUsuarioAssinatura2,
  });

  return (
    <div className="resultados-sequencial-page space-y-6">
      <section className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Resultados sequencial</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Selecione a empresa e a obra, escolha as datas de concretagem e gere o resultado sequencial.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="seq-empresa" className="text-sm font-medium text-slate-700">
              Empresa <span className="text-rose-500">*</span>
            </label>
            <select
              id="seq-empresa"
              value={selectedEmpresaId}
              onChange={handleEmpresaChange}
              disabled={loading}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">{loading ? 'Carregando...' : 'Selecione'}</option>
              {empresas.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="seq-obra" className="text-sm font-medium text-slate-700">
              Obra <span className="text-rose-500">*</span>
            </label>
            <select
              id="seq-obra"
              value={selectedObraId}
              onChange={handleObraChange}
              disabled={!selectedEmpresaId}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">
                {!selectedEmpresaId
                  ? 'Selecione uma empresa primeiro'
                  : obrasDaEmpresa.length === 0
                    ? 'Nenhuma obra para esta empresa'
                    : 'Selecione'}
              </option>
              {obrasDaEmpresa.map((obra) => (
                <option key={obra.id} value={obra.id}>{obra.nome}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="seq-identificacao" className="text-sm font-medium text-slate-700">
              Identificação do relatório
            </label>
            <input
              id="seq-identificacao"
              type="text"
              value={identificacaoRelatorio}
              onChange={(event) => setIdentificacaoRelatorio(event.target.value)}
              placeholder="Ex: nº 001/2026"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>

          <p className="text-xs text-slate-400">
            As assinaturas do rodapé vêm dos usuários vinculados no cadastro da empresa.
          </p>
        </div>
      </section>

      {selectedObraId ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Datas de concretagem</h3>
            <span className="text-xs text-slate-500">
              {selectedConcretagemIds.length} selecionada(s)
            </span>
          </div>

          {loadingElegibilidade ? (
            <p className="mt-4 text-sm text-slate-500">Carregando datas disponíveis...</p>
          ) : concretagensElegiveis.length === 0 ? (
            <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Nenhuma concretagem com resultado disponível para esta obra.
            </p>
          ) : (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {concretagensElegiveis.map((concretagem) => {
                const checked = selectedConcretagemIds.includes(concretagem.id);
                return (
                  <label
                    key={concretagem.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition ${checked
                      ? 'border-[#0e7490] bg-[#0e7490]/5 text-slate-800'
                      : 'border-slate-200 text-slate-600 hover:border-blue-200'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleConcretagem(concretagem.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                    />
                    <span>{formatDataHora(concretagem.dataConcretagem)}</span>
                  </label>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      {canGerar ? (
        <div className="flex flex-col items-end gap-2">
          {erroResultado ? (
            <p className="text-sm text-rose-600">{erroResultado}</p>
          ) : null}
          <PDFDownloadLink
            document={(
              <EnsaioCompressaoPDF
                resultados={resultadoData}
                cabecalho={cabecalhoPdf}
                corAtiva={corAtiva}
                logoCliente={logoCliente}
                assinaturas={assinaturas}
              />
            )}
            fileName={`resultados-sequencial-${selectedObraId}.pdf`}
            style={{
              textDecoration: 'none',
              pointerEvents: loadingResultado || carregandoPersonalizacao || resultadoData.length === 0 ? 'none' : 'auto',
            }}
            className={`inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 ${
              loadingResultado || carregandoPersonalizacao || resultadoData.length === 0 ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            {({ loading, error }) => {
              if (error) {
                console.error('Falha ao gerar PDF do relatório sequencial:', error);
              }
              return (
                <>
                  <FileBarChart className="h-4 w-4" />
                  {carregandoPersonalizacao
                    ? 'Preparando...'
                    : loading || loadingResultado
                      ? 'Gerando...'
                      : error
                        ? 'Falha ao gerar PDF'
                        : 'Gerar resultados'}
                </>
              );
            }}
          </PDFDownloadLink>
        </div>
      ) : null}
    </div>
  );
}

export default ResultadosSequencialPage;
